import React from "react";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

type Props = {
  label: string;
  value: string | number;
  unit?: string;
  delta?: number; // percent vs prior
  trend?: number[]; // sparkline series
  intent?: "positive" | "negative" | "neutral";
};

export function StatCard({ label, value, unit, delta, trend, intent = "neutral" }: Props) {
  const positive = (delta ?? 0) >= 0;
  const goodDirection =
    intent === "negative" ? !positive : intent === "positive" ? positive : positive;
  const deltaColor = goodDirection ? "text-el-700" : "text-rose-600";

  return (
    <div className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-eyebrow">{label}</div>
          <div className="mt-1 flex items-baseline gap-1.5">
            <div className="text-[1.6rem] leading-none font-semibold tracking-tightish text-slate-900">
              {value}
            </div>
            {unit && <div className="text-xs text-slate-500">{unit}</div>}
          </div>
          {delta !== undefined && (
            <div className={`mt-1.5 flex items-center text-xs ${deltaColor}`}>
              {positive ? <ArrowUpRight className="h-3.5 w-3.5" /> : <ArrowDownRight className="h-3.5 w-3.5" />}
              {Math.abs(delta).toFixed(1)}% <span className="text-slate-400 ml-1">vs last week</span>
            </div>
          )}
        </div>
        {trend && <Sparkline values={trend} />}
      </div>
    </div>
  );
}

export function Sparkline({ values }: { values: number[] }) {
  const w = 96, h = 32, p = 2;
  const min = Math.min(...values), max = Math.max(...values);
  const range = max - min || 1;
  const step = (w - p * 2) / (values.length - 1);
  const pts = values
    .map((v, i) => `${p + i * step},${h - p - ((v - min) / range) * (h - p * 2)}`)
    .join(" ");
  const last = values[values.length - 1];
  const lastX = p + (values.length - 1) * step;
  const lastY = h - p - ((last - min) / range) * (h - p * 2);

  return (
    <svg width={w} height={h} className="shrink-0">
      <defs>
        <linearGradient id="spark-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%"   stopColor="#10b981" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline
        fill="url(#spark-fill)"
        stroke="none"
        points={`${p},${h - p} ${pts} ${w - p},${h - p}`}
      />
      <polyline
        points={pts}
        fill="none"
        stroke="#10b981"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx={lastX} cy={lastY} r="2.2" fill="#10b981" />
    </svg>
  );
}
